import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric18,
  type DashboardMetric18AuditorCoverage,
} from '@/services/dashboardService';

const { Text } = Typography;

export type EX1AuditorCoverageForRemainingWorkStackedBarProps = {
  height?: number;
  title?: string;
};

type Datum = {
  name: string;
  assigned: number;
  unassigned: number;
};

const AssignedColor = '#22C55E'; // Green
const UnassignedColor = '#EF4444'; // Red

const AuditorCoverageTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const p = payload?.[0]?.payload as Datum | undefined;
  if (!p) return null;

  const total = (p.assigned ?? 0) + (p.unassigned ?? 0);
  const assignedPct = total > 0 ? (p.assigned / total) * 100 : 0;
  const unassignedPct = total > 0 ? (p.unassigned / total) * 100 : 0;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 230,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        Auditor Coverage (In-Scope)
      </div>

      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>Assigned</span>
        <span style={{ fontWeight: 600 }}>
          {p.assigned} ({assignedPct.toFixed(1)}%)
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 4,
        }}
      >
        <span>Unassigned</span>
        <span style={{ fontWeight: 600 }}>
          {p.unassigned} ({unassignedPct.toFixed(1)}%)
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 8,
        }}
      >
        <span>Total</span>
        <span style={{ fontWeight: 600 }}>{total}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a segment to view controls in Exhibit 5
      </div>
    </div>
  );
};

export const EX1AuditorCoverageForRemainingWorkStackedBar: React.FC<
  EX1AuditorCoverageForRemainingWorkStackedBarProps
> = ({ height = 160, title = 'Auditor Coverage for Remaining Work' }) => {
  const navigate = useNavigate();

  // annualReportId from Redux
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric18AuditorCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw(null);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const res = await getDashboardMetric18(annualReportId);
        if (!isMounted) return;
        setRaw(res);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw(null);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 18 (Auditor Coverage).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const { totalControls, unassignedControls, assignedControls } =
    useMemo(() => {
      const total = raw?.totalControls ?? 0;
      const unassigned = raw?.unassignedControls ?? 0;
      const assigned = Math.max(0, total - unassigned);

      return {
        totalControls: total,
        unassignedControls: Math.max(0, unassigned),
        assignedControls: assigned,
      };
    }, [raw]);

  const data: Datum[] = useMemo(
    () => [
      {
        name: 'Coverage',
        assigned: assignedControls,
        unassigned: unassignedControls,
      },
    ],
    [assignedControls, unassignedControls],
  );

  const isEmpty = !loading && !errorMessage && (totalControls ?? 0) === 0;

  // Navigate to Exhibit 5 with filters
  const goToControlActivities = (hasAuditorAssigned: boolean) => {
    navigate('/control-activities', {
      state: {
        testingScope: true, // In Scope
        hasAuditorAssigned,
      },
    });
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 18 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No in-scope controls were returned.'
            showIcon
          />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: UnassignedColor,
                }}
              >
                {unassignedControls} Controls Unassigned
              </div>
            </div>

            <ResponsiveContainer width='100%' height={90}>
              <BarChart
                data={data}
                layout='vertical'
                margin={{ top: 0, right: 16, bottom: 0, left: 16 }}
              >
                <XAxis
                  type='number'
                  hide
                  domain={[0, Math.max(1, totalControls)]}
                />
                <YAxis type='category' dataKey='name' hide />
                <Tooltip content={<AuditorCoverageTooltip />} />

                <Bar
                  dataKey='assigned'
                  stackId='a'
                  isAnimationActive={false}
                  onClick={() => {
                    if (assignedControls > 0) goToControlActivities(true);
                  }}
                  style={{
                    cursor: assignedControls > 0 ? 'pointer' : 'default',
                  }}
                >
                  <Cell fill={AssignedColor} />
                </Bar>

                <Bar
                  dataKey='unassigned'
                  stackId='a'
                  isAnimationActive={false}
                  onClick={() => {
                    if (unassignedControls > 0) goToControlActivities(false);
                  }}
                  style={{
                    cursor: unassignedControls > 0 ? 'pointer' : 'default',
                  }}
                >
                  <Cell fill={UnassignedColor} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <Text type='secondary'>
                Assigned: {assignedControls} • Unassigned: {unassignedControls}{' '}
                • Total: {totalControls}
              </Text>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default EX1AuditorCoverageForRemainingWorkStackedBar;
